import SaveToGooglePayButton from '@google-pay/save-button-react';
import isEmpty from 'lodash/isEmpty';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useState, useEffect } from 'react';
import Cookies from 'universal-cookie';

import { postGoogleWallet, postAppleWallet } from '../api/Wallet';
import { createAddressUser } from '../api/wave/users';
import AddToAppleWallet from '../components/AddToAppleWallet/AddToAppleWallet';
import { PrimaryButton } from '../components/Buttons/Buttons';
import ClubCardV2 from '../components/ClubCards/ClubCardV2';
import CustomHead from '../components/CustomHead';
import MainLogo from '../components/MainLogo/MainLogo';
import useDeviceDetect from '../hooks/useDeviceDetect';
import Loader from '../store/loader';
import User from '../store/user';
import { addToAnalytics } from '../utils/analytics';

import styles from './digital-card-success.module.scss';

export default function DigitalCardSuccess() {
  const router = useRouter();
  const [user, setUser] = useState(null);
  const [origin, setOrigin] = useState('');
  const [step, setStep] = useState('success');
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [walletError, setWalletError] = useState(false);
  const [walletErrorMessage, setWalletErrorMessage] = useState('');
  const { isAndroid, isIos } = useDeviceDetect();
  const [googleJWT, setGoogleJWT] = useState({});

  const cookies = new Cookies();
  const AddressView = dynamic(() => import('../components/GenericModal/AddressView/AddressView'), { ssr: false });
  async function addAddressRequest(newAddress) {
    setError('');
    addToAnalytics('addAddres', undefined, [['option', 'submit']]);
    const addressToCreate = { address: [{ ...newAddress }] };
    const token = cookies.get('token');
    const response = await createAddressUser(token, addressToCreate);
    if (typeof response === 'string' || response?.error) {
      setError(true);
      setErrorMessage(response.error);
      return;
    }
    User.addAddress(response?.address);
    router.push('/', '/');
  }
  useEffect(() => {
    if (router.isReady) {
      setOrigin(router.query.origin);
    }
  }, [router.isReady]);

  useEffect(() => {
    const user$ = User.subscribe(async _user => {
      if (isEmpty(_user)) {
        router.replace('/');
        return;
      }
      setUser(_user);
    });
    return () => user$.unsubscribe();
  }, []);

  useEffect(() => {
    async function addToGoogleWallet() {
      Loader.addEvent('GOOGLE_WALLET');
      const { googleJwt, gWalletError } = await postGoogleWallet(user?.loyaltyCards[0].cardId);
      if (!isEmpty(googleJwt)) {
        setGoogleJWT(googleJwt);
      } else {
        setWalletError(true);
        setWalletErrorMessage(gWalletError);
      }
      Loader.removeEvent('GOOGLE_WALLET');
    }
    if (isAndroid) addToGoogleWallet();
  }, [isAndroid]);

  async function addToAppleWallet() {
    const { applePassWallet, applePassError } = await postAppleWallet(user?.loyaltyCards[0].cardId);
    if (isEmpty(applePassWallet) && applePassError) {
      setWalletError(true);
      setWalletErrorMessage(applePassError);
    }
  }

  function clickSuccess() {
    if (origin === 'register') {
      setStep('address');
    } else {
      router.push('/');
    }
  }

  if (!user?._id) return null;
  return (
    <section className={step === 'address' ? styles.addressContainer : styles.container}>
      <CustomHead title="Σύνδεση Digital Card/ Εγγραφή Digital Card" />
      <div className={styles.logoContainer}>
        <Link href="/">
          <a>
            <MainLogo />
          </a>
        </Link>
      </div>
      {step === 'success' && (
        <>
          <h1 className={styles.header}>{origin === 'login' ? 'Βρήκαμε την κάρτα σου!' : 'Αυτή είναι η κάρτα σου!'}</h1>
          <div className={styles.cardContainer}>
            <ClubCardV2
              name={user?.fullName}
              points={user?.loyaltyCards[0].points}
              cardNumber={user?.loyaltyCards[0].cardId}
            />
          </div>
          <p className={styles.p}>
            {origin === 'register'
              ? 'Μόλις απέκτησες τη δική σου ψηφιακή κάρτα και μπορείς πλέον να την έχεις πάντα μαζί στο κινητό σου'
              : 'Η κάρτα σου μόλις μετατράπηκε σε ψηφιακή για να την έχεις πάντα μαζί στο κινητό σου.'}
          </p>
          {isAndroid && !walletError && (
            <div className={styles.addToGPayContainer}>
              <SaveToGooglePayButton jwt={googleJWT} size="matchparent" height="standard" />
            </div>
          )}
          {isIos && (
            <div className={styles.walletImageContainer}>
              <AddToAppleWallet addToAppleWallet={addToAppleWallet} />
            </div>
          )}

          <div className={styles.primaryButtonContainer}>
            <a className={styles.fullWidth}>
              <PrimaryButton onClick={() => clickSuccess()} data-cy="success-digital" style={{ width: '100%' }}>
                {origin === 'register' ? 'Δες πώς εξυπηρετείσαι' : 'Συνέχισε τις αγορές σου!'}
              </PrimaryButton>
            </a>
          </div>
          {walletError && <p className={styles.errorSmallText}>{walletErrorMessage}</p>}
        </>
      )}
      {origin === 'register' && step === 'address' && (
        <>
          <h1 className={styles.addressHeader}>Προσθήκη Διεύθυνσης</h1>
          <p className={styles.addressText}>Συμπλήρωσε τα στοιχεία της διεύθυνσής σου και δες πώς εξυπηρετείσαι</p>

          <AddressView submitForm={addAddressRequest} />
          {error && <p className={styles.errorSmallText}>{errorMessage}</p>}
        </>
      )}
    </section>
  );
}
